
'use client';

import { useEffect, useState } from 'react';
import { errorEmitter } from '@/firebase/error-emitter';
import type { FirestorePermissionError } from '@/firebase/errors';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { X, ServerCrash } from 'lucide-react';
import { Button } from './ui/button';

function getOperationVerb(operation: string) {
  switch(operation) {
    case 'get': return 'read';
    case 'list': return 'list';
    case 'create': return 'create';
    case 'update': return 'update';
    case 'delete': return 'delete';
    default: return 'perform';
  }
}

function getRuleSuggestion(error: FirestorePermissionError) {
  const { operation, path } = error.context;
  const parts = path.split('/').filter(Boolean);
  
  if (parts.length < 2) {
    return 'Invalid path structure.';
  }

  const collection = parts[0];
  const docId = parts[1];

  let allowStatement = '';

  if (operation === 'get' || operation === 'list') {
    allowStatement = 'allow read';
  } else if (operation === 'create' || operation === 'update' || operation === 'delete') {
    allowStatement = 'allow write';
  }

  if (docId.startsWith('{') && docId.endsWith('}')) {
    const varName = docId.slice(1, -1);
    if (varName.toLowerCase().includes('user')) {
      return `match /${collection}/{${varName}} {\n  ${allowStatement}: if request.auth.uid == ${varName};\n}`;
    }
  }

  return `match /${collection}/{documentId} {\n  ${allowStatement}: if request.auth != null;\n}`;
}


export function FirebaseErrorListener() {
  const [error, setError] = useState<FirestorePermissionError | null>(null);

  useEffect(() => {
    const handleError = (e: FirestorePermissionError) => {
      // Ignore expected permission errors that have fallback mechanisms
      const ignoredPaths = [
        'projectSummaries', // Has legacy fallback to 'projects' collection
      ];
      
      const shouldIgnore = ignoredPaths.some(path => 
        e.context.path.includes(path)
      );
      
      if (shouldIgnore) {
        console.debug('Ignoring expected permission error with fallback:', e.context.path);
        return;
      }
      
      console.info(
        "Caught a Firestore permission error. Displaying contextual information."
      );
      setError(e);
    };

    errorEmitter.on('permission-error', handleError);

    return () => {
      errorEmitter.off('permission-error', handleError);
    };
  }, []);

  if (!error) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="relative max-w-3xl w-full bg-background rounded-lg shadow-2xl border border-destructive/50">
        <Button 
            variant="ghost" 
            size="icon" 
            className="absolute top-2 right-2 h-8 w-8"
            onClick={() => setError(null)}
        >
          <X className="h-5 w-5" />
        </Button>
        
        <div className="p-6 sm:p-8">
            <div className="flex items-start gap-4">
                <div className="p-2 bg-destructive/10 rounded-full">
                    <ServerCrash className="h-8 w-8 text-destructive" />
                </div>
                <div>
                    <h2 className="text-2xl font-headline text-destructive">Permission Denied</h2>
                    <p className="text-muted-foreground mt-1">
                        Your request to Firestore was denied by security rules.
                    </p>
                </div>
            </div>

            <div className="mt-6 space-y-4 text-sm">
                <div className="flex flex-col sm:flex-row gap-1 sm:gap-4">
                    <strong className="w-28 shrink-0 text-muted-foreground">Operation</strong>
                    <div className="font-mono flex items-center gap-2">
                        <Badge variant="destructive">{error.context.operation.toUpperCase()}</Badge>
                        <span>{getOperationVerb(error.context.operation)}</span>
                    </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-1 sm:gap-4">
                    <strong className="w-28 shrink-0 text-muted-foreground">Path</strong>
                    <span className="font-mono text-foreground break-all">{error.context.path}</span>
                </div>
            </div>

            {error.context.requestResourceData && (
                <div className="mt-6">
                <strong className="text-muted-foreground text-sm">Request Data</strong>
                <pre className="mt-2 p-3 bg-muted rounded-md text-xs overflow-auto max-h-40">
                    <code>{JSON.stringify(error.context.requestResourceData, null, 2)}</code>
                </pre>
                </div>
            )}
            
            <Alert variant="default" className="mt-8">
                <AlertTitle className="font-semibold">Suggested Security Rule</AlertTitle>
                <AlertDescription>
                    To allow this request, you could add the following to your <code className="font-mono text-xs bg-muted p-1 rounded-md">firestore.rules</code> file:
                <pre className="mt-2 p-3 bg-muted rounded-md text-xs overflow-auto">
                    <code>{getRuleSuggestion(error)}</code>
                </pre>
                </AlertDescription>
            </Alert>
        </div>
      </div>
    </div>
  );
}
